import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Shield, Loader2, LogOut, Eye, EyeOff, Download, Edit, XCircle, Gift, Plus, TrendingUp, Users, DollarSign, Activity, BarChart3, Trash2, Crown, UserPlus, Brain, Calendar, RefreshCw, LogIn, X, Table2, Search, Clock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';

// OPARA v7.0 - Sacred Domain Detection
import { BACKEND_URL } from "@/utils/apiConfig";

// OPARA v28.0 - Reels Snapshot Modal (Admin Only)
import ReelsSnapshotModal from "@/components/HomePage/ReelsSnapshotModal";

function AdminPage() {
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");          // C2: admin login (email + master sifre + 2FA)
  const [show2FA, setShow2FA] = useState(false);   // C2: 2FA adimi gorunur
  const [totpCode, setTotpCode] = useState("");    // C2: 6-hane TOTP
  const [showPassword, setShowPassword] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [adminToken, setAdminToken] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [topInfluencers, setTopInfluencers] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [conversionRates, setConversionRates] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Gift modal states
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [giftEmail, setGiftEmail] = useState("");
  const [giftPassword, setGiftPassword] = useState("");
  const [giftFirstName, setGiftFirstName] = useState("");
  const [giftLastName, setGiftLastName] = useState("");
  const [giftQuota, setGiftQuota] = useState(10);
  const [giftDuration, setGiftDuration] = useState(30);
  const [giftLoading, setGiftLoading] = useState(false);
  
  // Delete user modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Credit top-up modal states (admin +kredi miktar-secimi)
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [creditUser, setCreditUser] = useState(null);
  const [creditAmount, setCreditAmount] = useState("10");

  // Whitelist states
  const [whitelist, setWhitelist] = useState([]);
  const [whitelistLoading, setWhitelistLoading] = useState(false);
  const [newWhitelistEmail, setNewWhitelistEmail] = useState("");
  const [addingWhitelist, setAddingWhitelist] = useState(false);
  const [removingWhitelistEmail, setRemovingWhitelistEmail] = useState(null);
  
  // OPARA v30.1: URL-based tab control
  const tabFromUrl = searchParams.get('tab');
  const validTabs = ['whitelist', 'dashboard', 'userstats', 'reels'];
  const initialTab = validTabs.includes(tabFromUrl) ? tabFromUrl : 'whitelist';
  const [activeTab, setActiveTab] = useState(initialTab);

  // OPARA v28.1: User Stats states
  const [userStats, setUserStats] = useState(null);
  const [userStatsLoading, setUserStatsLoading] = useState(false);
  const [statsDateRange, setStatsDateRange] = useState({
    from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });
  const [selectedUserDetail, setSelectedUserDetail] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // OPARA v28.0: Reels Snapshot states
  const [showReelsSnapshot, setShowReelsSnapshot] = useState(false);
  const [reelsSnapshotUsername, setReelsSnapshotUsername] = useState("");
  const [reelsSnapshotSearchInput, setReelsSnapshotSearchInput] = useState("");
  const [reelsSnapshotLoading, setReelsSnapshotLoading] = useState(false);

  // OPARA v30.0: Tarih Filtresi ve Analiz Listesi
  const [dateFilter, setDateFilter] = useState("today"); // today, yesterday, 7days, 15days, 30days, 90days, custom
  const [customDateRange, setCustomDateRange] = useState({ from: "", to: "" });
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // OPARA v30.6: Kullanıcı Ara özelliği
  const [userSearchEmail, setUserSearchEmail] = useState("");
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  
  // OPARA v30.7: Son 10 Analiz
  const [last10Analyses, setLast10Analyses] = useState([]);
  const [last10Loading, setLast10Loading] = useState(false);

  useEffect(() => {
    // Check if admin token exists in localStorage
    const token = localStorage.getItem('admin_token');
    if (token) {
      setAdminToken(token);
      setIsAuthenticated(true);
      fetchUsers(token);
      fetchAnalytics(token);
      fetchWhitelist(token);
    }
  }, []);
  
  // OPARA v30.7: Admin giriş yaptıktan sonra Son 10 Analiz çek
  useEffect(() => {
    if (isAuthenticated && adminToken) {
      fetchLast10Analyses();
    }
  }, [isAuthenticated, adminToken]);

  // Commit B: admin token alindiginda oturumu baslat (her iki giris yolu da kullanir)
  const activateAdminSession = async (access_token) => {
    localStorage.setItem('admin_token', access_token);
    setAdminToken(access_token);
    setIsAuthenticated(true);
    toast.success("Admin girişi başarılı!");

    await fetchUsers(access_token);
    await fetchAnalytics(access_token);
    await fetchWhitelist(access_token);
    fetchLast10Analyses();  // OPARA v30.7: Son 10 analiz
  };

  // C2: admin girisi - email + MASTER sifre + 2FA -> /api/admin/login (backend C1 yeni yol)
  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = { email, password };
      if (show2FA && totpCode) payload.totp_code = totpCode;

      const response = await axios.post(`${BACKEND_URL}/api/admin/login`, payload);

      // 2FA gerekiyorsa: kod adimini goster, ayni endpoint'e re-post
      if (response.data.requires_2fa) {
        setShow2FA(true);
        toast.info("Google Authenticator kodunuzu girin");
        return;
      }

      const access_token = response.data.access_token;
      if (!access_token) {
        toast.error("Giriş başarısız");
        return;
      }

      // Token /admin/login'den geliyor (zaten role:admin); admin-degil reddi backend'de (is_admin_user).
      await activateAdminSession(access_token);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail?.message || detail || "Giriş başarısız");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async (token) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setUsers(response.data);
    } catch (error) {
      toast.error("Kullanıcılar yüklenemedi");
      console.error(error);
    }
  };

  const fetchAnalytics = async (token) => {
    setAnalyticsLoading(true);
    try {
      const [overviewRes, influencersRes, usersRes, conversionRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/analytics/overview`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/admin/analytics/top-influencers?limit=10`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/admin/analytics/user-activity?limit=10`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/admin/analytics/conversion-rates`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      setAnalytics(overviewRes.data);
      setTopInfluencers(influencersRes.data.top_influencers || []);
      setActiveUsers(usersRes.data.active_users || []);
      setConversionRates(conversionRes.data);
    } catch (error) {
      toast.error("Analizler yüklenemedi");
      console.error(error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setAdminToken(null);
    setIsAuthenticated(false);
    setUsers([]);
    setPassword("");
    setWhitelist([]);
    toast.success("Çıkış yapıldı");
  };

  // Whitelist functions
  const fetchWhitelist = async (token) => {
    setWhitelistLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/whitelist`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setWhitelist(response.data.whitelist || []);
    } catch (error) {
      console.error("Whitelist fetch error:", error);
    } finally {
      setWhitelistLoading(false);
    }
  };

  // OPARA v28.1: User Stats fetch function
  const fetchUserStats = async (token, fromDate = null, toDate = null) => {
    setUserStatsLoading(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/user-stats?${params.toString()}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setUserStats(response.data);
    } catch (error) {
      console.error("User stats fetch error:", error);
      toast.error("Kullanıcı istatistikleri yüklenemedi");
      setUserStats({ success: false, users: [], summary: { active_users: 0, total_logins: 0, total_analyses: 0 } });
    } finally {
      setUserStatsLoading(false);
    }
  };

  // Handle date range change
  const handleDateRangeChange = (field, value) => {
    const newRange = { ...statsDateRange, [field]: value };
    setStatsDateRange(newRange);
    if (adminToken) {
      fetchUserStats(adminToken, newRange.from, newRange.to);
    }
  };

  // OPARA v30.0: Tarih filtresine göre analiz geçmişini çek
  const fetchAnalysisHistory = async (filterType, customFrom = null, customTo = null) => {
    setHistoryLoading(true);
    try {
      const now = new Date();
      let fromDate, toDate;
      
      // Tarih aralığını hesapla
      switch (filterType) {
        case 'today':
          fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          toDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
          break;
        case 'yesterday':
          fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
          toDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59);
          break;
        case '7days':
          fromDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          toDate = now;
          break;
        case '15days':
          fromDate = new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000);
          toDate = now;
          break;
        case '30days':
          fromDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          toDate = now;
          break;
        case '90days':
          fromDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          toDate = now;
          break;
        case 'custom':
          if (customFrom) fromDate = new Date(customFrom);
          if (customTo) toDate = new Date(customTo + 'T23:59:59');
          break;
        default:
          fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          toDate = now;
      }
      
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate.toISOString());
      if (toDate) params.append('to_date', toDate.toISOString());
      
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/analysis-history?${params.toString()}`,
        { headers: { 'Authorization': `Bearer ${adminToken}` } }
      );
      
      // OPARA v30.6: Aynı gün + aynı influencer = 1 kayıt (deduplikasyon)
      // En son analizi göster, dropdown'dan diğerlerine erişilebilir
      const rawAnalyses = response.data.analyses || [];
      const groupedMap = new Map();
      
      rawAnalyses.forEach(analysis => {
        const queryDate = new Date(analysis.query_time);
        const dateKey = `${queryDate.getFullYear()}-${queryDate.getMonth()}-${queryDate.getDate()}`;
        const uniqueKey = `${analysis.username}_${dateKey}`;
        
        // Her influencer+gün kombinasyonu için sadece en son analizi tut
        if (!groupedMap.has(uniqueKey) || new Date(analysis.query_time) > new Date(groupedMap.get(uniqueKey).query_time)) {
          groupedMap.set(uniqueKey, analysis);
        }
      });
      
      // Map'ten array'e dönüştür ve tarihe göre sırala (en yeni önce)
      const deduplicatedAnalyses = Array.from(groupedMap.values())
        .sort((a, b) => new Date(b.query_time) - new Date(a.query_time));
      
      setAnalysisHistory(deduplicatedAnalyses);
    } catch (error) {
      console.error("Analysis history fetch error:", error);
      toast.error("Analiz geçmişi yüklenemedi");
      setAnalysisHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Tarih filtresi değiştiğinde
  const handleDateFilterChange = (filterType) => {
    setDateFilter(filterType);
    if (filterType !== 'custom') {
      fetchAnalysisHistory(filterType);
    }
  };

  // Custom tarih seçildiğinde
  const handleCustomDateSearch = () => {
    if (customDateRange.from) {
      fetchAnalysisHistory('custom', customDateRange.from, customDateRange.to || customDateRange.from);
    } else {
      toast.error("Lütfen başlangıç tarihi seçin");
    }
  };

  // OPARA v30.6: Kullanıcı email'ine göre arama
  const handleUserSearch = async () => {
    if (!userSearchEmail.trim()) {
      toast.error("Lütfen bir email adresi girin");
      return;
    }
    
    setUserSearchLoading(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/analysis-history?user_email=${encodeURIComponent(userSearchEmail.trim())}`,
        { headers: { 'Authorization': `Bearer ${adminToken}` } }
      );
      
      setUserSearchResults(response.data.analyses || []);
      if ((response.data.analyses || []).length === 0) {
        toast.info("Bu kullanıcı için analiz bulunamadı");
      }
    } catch (error) {
      console.error("User search error:", error);
      toast.error("Kullanıcı araması başarısız");
      setUserSearchResults([]);
    } finally {
      setUserSearchLoading(false);
    }
  };

  // OPARA v30.7: Son 10 Analiz çek (deduplikasyon YOK)
  const fetchLast10Analyses = async () => {
    setLast10Loading(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/last-10-analyses`,
        { headers: { 'Authorization': `Bearer ${adminToken}` } }
      );
      
      setLast10Analyses(response.data.analyses || []);
    } catch (error) {
      console.error("Last 10 analyses fetch error:", error);
      setLast10Analyses([]);
    } finally {
      setLast10Loading(false);
    }
  };

  // Show user detail modal
  const handleShowUserDetail = (user) => {
    setSelectedUserDetail(user);
    setShowDetailModal(true);
  };

  const handleAddToWhitelist = async (e) => {
    e.preventDefault();
    
    if (!newWhitelistEmail.trim()) {
      toast.error("E-posta adresi girin");
      return;
    }
    
    if (!newWhitelistEmail.toLowerCase().endsWith("@personaajans.com")) {
      toast.error("Sadece @personaajans.com uzantılı e-postalar eklenebilir");
      return;
    }
    
    setAddingWhitelist(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/whitelist`,
        { email: newWhitelistEmail.toLowerCase() },
        { headers: { 'Authorization': `Bearer ${adminToken}` } }
      );
      
      toast.success(`${newWhitelistEmail} beyaz listeye eklendi`);
      setNewWhitelistEmail("");
      fetchWhitelist(adminToken);
    } catch (error) {
      console.error("Add whitelist error:", error);
      const message = error.response?.data?.detail || "E-posta eklenemedi";
      toast.error(message);
    } finally {
      setAddingWhitelist(false);
    }
  };

  const handleRemoveFromWhitelist = async (email) => {
    if (!window.confirm(`${email} adresini beyaz listeden çıkarmak istediğinize emin misiniz?`)) {
      return;
    }
    
    setRemovingWhitelistEmail(email);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/whitelist/${encodeURIComponent(email)}`,
        { headers: { 'Authorization': `Bearer ${adminToken}` } }
      );
      
      toast.success(`${email} beyaz listeden çıkarıldı`);
      fetchWhitelist(adminToken);
    } catch (error) {
      console.error("Remove whitelist error:", error);
      const message = error.response?.data?.detail || "E-posta çıkarılamadı";
      toast.error(message);
    } finally {
      setRemovingWhitelistEmail(null);
    }
  };

  const handleExportUsers = async () => {
    try {
      toast.loading("Üye listesi indiriliyor...");
      
      const response = await axios.get(`${BACKEND_URL}/api/admin/export-users`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `uyeler_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.dismiss();
      toast.success("Üye listesi indirildi!");
    } catch (error) {
      toast.dismiss();
      toast.error("İndirme başarısız!");
      console.error(error);
    }
  };

  const handleExportQueries = async () => {
    try {
      toast.loading("Sorgu geçmişi indiriliyor...");
      
      const response = await axios.get(`${BACKEND_URL}/api/admin/export-queries`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sorgular_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.dismiss();
      toast.success("Sorgu geçmişi indirildi!");
    } catch (error) {
      toast.dismiss();
      toast.error("İndirme başarısız!");
      console.error(error);
    }
  };

  const handleUpdateQuota = async (userId, quotaChange) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/update-quota`,
        {
          user_id: userId,
          quota_change: quotaChange
        },
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      toast.success(response.data.message);
      // Refresh users list
      await fetchUsers(adminToken);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Sorgu hakkı güncellenemedi");
    }
  };

  const handleCancelSubscription = async (userId, userEmail) => {
    if (!window.confirm(`Cancel subscription for ${userEmail}? Billing stops; access continues until the end of the current period.`)) {
      return;
    }

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/cancel-subscription`,
        {
          user_id: userId
        },
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      toast.success(response.data.message);
      // Refresh users list
      await fetchUsers(adminToken);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Üyelik iptal edilemedi");
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    setDeleteLoading(true);
    try {
      const response = await axios.delete(
        `${BACKEND_URL}/api/admin/users/${userToDelete.id}`,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      toast.success(`${userToDelete.email} kullanıcısı silindi`);
      setShowDeleteModal(false);
      setUserToDelete(null);
      // Refresh users list
      await fetchUsers(adminToken);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kullanıcı silinemedi");
    } finally {
      setDeleteLoading(false);
    }
  };

  const openDeleteModal = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  // Admin +kredi: miktar-secim modalini ac (delete-modal deseni). Miktar modal'dan secilir -> handleUpdateQuota (dinamik).
  const openCreditModal = (user) => {
    setCreditUser(user);
    setCreditAmount("10");
    setShowCreditModal(true);
  };

  const handleGiftQuota = async (e) => {
    e.preventDefault();
    setGiftLoading(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/gift-quota`,
        {
          email: giftEmail,
          password: giftPassword,
          first_name: giftFirstName,
          last_name: giftLastName,
          quota: parseInt(giftQuota),
          duration_days: parseInt(giftDuration)
        },
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      toast.success(response.data.message);
      // Reset form
      setGiftEmail("");
      setGiftPassword("");
      setGiftFirstName("");
      setGiftLastName("");
      setGiftQuota(10);
      setGiftDuration(30);
      setShowGiftModal(false);
      // Refresh users list
      await fetchUsers(adminToken);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Hediye sorgu oluşturulamadı");
    } finally {
      setGiftLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-gray-900 border-2 border-red-500">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center">
                <Shield className="w-10 h-10 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl text-red-500">Admin Panel</CardTitle>
          </CardHeader>
          <CardContent>
            {/* C2: TEK form - email + MASTER sifre + 2FA -> /api/admin/login */}
            <form onSubmit={handleAdminLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">E-posta</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@personaajans.com"
                  required
                  disabled={show2FA}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Master Şifre</label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••"
                    required
                    disabled={show2FA}
                    className="bg-gray-800 border-gray-700 text-white pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              {show2FA && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">2FA Kodu</label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="123456"
                    required
                    className="bg-gray-800 border-gray-700 text-white text-center tracking-widest"
                  />
                </div>
              )}
              <Button type="submit" disabled={loading} className="w-full bg-red-500 hover:bg-red-600 text-white font-bold">
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Giriş Yapılıyor...</>
                ) : (show2FA ? "Doğrula ve Gir" : "Giriş Yap")}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black">
      {/* Ana Sayfaya Dön & Çıkış - Sağ Üst */}
      <div className="absolute top-4 right-4 z-20 flex gap-3">
        <Button
          onClick={() => navigate("/")}
          className="bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold"
        >
          Ana Sayfaya Dön
        </Button>
        <Button
          onClick={handleLogout}
          variant="outline"
          className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Çıkış
        </Button>
      </div>

      <div className="relative z-10 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Admin Navigation - Sticky at top */}
          <div className="sticky top-0 z-50 bg-[#0D0D0D]/95 backdrop-blur-sm py-4 mb-8 -mx-4 px-4">
            <div className="flex items-center justify-between">
              {/* Admin Panel Title - Sol */}
              <div className="flex items-center gap-3">
                <Shield className="w-8 h-8 text-red-500" />
                <h1 className="text-2xl font-bold text-red-500">Admin Panel</h1>
              </div>
              
              {/* Tabs - Orta */}
              <div className="bg-gray-800 rounded-lg p-1 flex gap-2">
                <button
                  onClick={() => setActiveTab("whitelist")}
                  className={`px-6 py-3 rounded-md font-bold transition-colors text-base ${
                    activeTab === "whitelist"
                      ? "bg-[#FFDD59] text-black"
                      : "text-gray-400 hover:text-white hover:bg-gray-700"
                  }`}
                >
                  <Users className="w-5 h-5 inline mr-2" />
                  Ekip Yönetimi
                  {whitelist.length > 0 && (
                    <span className={`ml-2 text-xs px-2 py-0.5 rounded-full font-bold ${
                      activeTab === "whitelist" ? "bg-black text-[#FFDD59]" : "bg-gray-600"
                    }`}>
                      {whitelist.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setActiveTab("dashboard")}
                  className={`px-6 py-3 rounded-md font-bold transition-colors text-base ${
                    activeTab === "dashboard"
                      ? "bg-red-500 text-white"
                      : "text-gray-400 hover:text-white hover:bg-gray-700"
                  }`}
                >
                  <Activity className="w-5 h-5 inline mr-2" />
                  Dashboard
                </button>
                <button
                  onClick={() => {
                    setActiveTab("userstats");
                    if (!userStats && adminToken) {
                      fetchUserStats(adminToken, statsDateRange.from, statsDateRange.to);
                    }
                  }}
                  className={`px-6 py-3 rounded-md font-bold transition-colors text-base ${
                    activeTab === "userstats"
                      ? "bg-blue-500 text-white"
                      : "text-gray-400 hover:text-white hover:bg-gray-700"
                  }`}
                >
                  <BarChart3 className="w-5 h-5 inline mr-2" />
                  Kullanıcı Analitik
                </button>
                <button
                  onClick={() => setActiveTab("reels")}
                  className={`px-6 py-3 rounded-md font-bold transition-colors text-base ${
                    activeTab === "reels"
                      ? "bg-yellow-500 text-black"
                      : "text-gray-400 hover:text-white hover:bg-gray-700"
                  }`}
                >
                  <Table2 className="w-5 h-5 inline mr-2" />
                  Reels Snapshot
                </button>
                <button
                  onClick={() => navigate("/admin/intelligence")}
                  className="px-6 py-3 rounded-md font-bold transition-colors text-base text-gray-400 hover:text-white hover:bg-gray-700"
                >
                  <Brain className="w-5 h-5 inline mr-2" />
                  Intelligence
                </button>
              </div>
              
              {/* Spacer - sağ tarafta boş alan */}
              <div className="w-48"></div>
            </div>
          </div>

          {/* WHITELIST TAB */}
          {activeTab === "whitelist" && (
            <div className="space-y-6">
              {/* Header */}
              <div className="bg-[#FFDD59]/10 border-2 border-[#FFDD59] rounded-lg p-6 mb-6">
                <h2 className="text-2xl font-bold text-[#FFDD59] mb-2">👥 Ekip Üyesi Yönetimi</h2>
                <p className="text-gray-300">
                  Buradan @personaajans.com uzantılı e-posta adreslerini ekip üyesi olarak ekleyebilirsiniz. 
                  Eklenen kişiler kayıt olduklarında <span className="text-[#FFDD59] font-bold">sınırsız analiz hakkı</span> alırlar.
                </p>
              </div>

              {/* Add New Staff Card */}
              <Card className="bg-gray-900 border-2 border-[#FFDD59]">
                <CardHeader>
                  <CardTitle className="text-[#FFDD59] flex items-center gap-2 text-xl">
                    <UserPlus className="w-6 h-6" />
                    Yeni Ekip Üyesi Ekle
                  </CardTitle>
                  <CardDescription className="text-gray-400 text-base">
                    Aşağıya ekip üyesinin @personaajans.com uzantılı e-posta adresini yazın ve "Ekle" butonuna tıklayın.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleAddToWhitelist} className="flex gap-3">
                    <Input
                      type="email"
                      placeholder="ornek@personaajans.com"
                      value={newWhitelistEmail}
                      onChange={(e) => setNewWhitelistEmail(e.target.value)}
                      className="flex-1 bg-gray-800 border-gray-700 text-white text-lg py-6"
                      disabled={addingWhitelist}
                    />
                    <Button
                      type="submit"
                      disabled={addingWhitelist || !newWhitelistEmail.trim()}
                      className="bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold text-lg px-8 py-6"
                    >
                      {addingWhitelist ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Plus className="w-5 h-5 mr-2" />
                          Ekle
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Whitelist Card */}
              <Card className="bg-gray-900 border-2 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2 text-xl">
                    <Users className="w-6 h-6 text-[#FFDD59]" />
                    Mevcut Yetkili Ekip Üyeleri
                    <span className="text-base text-gray-400 font-normal">
                      ({whitelist.length} kişi)
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {whitelistLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-[#FFDD59]" />
                    </div>
                  ) : whitelist.length === 0 ? (
                    <div className="text-center py-8 text-gray-400 text-lg">
                      Henüz ekip üyesi eklenmemiş. Yukarıdaki formu kullanarak ekleyebilirsiniz.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {whitelist.map((entry) => (
                        <div
                          key={entry.email}
                          className={`flex items-center justify-between p-4 rounded-lg ${
                            entry.is_primary 
                              ? 'bg-[#FFDD59]/20 border-2 border-[#FFDD59]' 
                              : 'bg-gray-800 border border-gray-700'
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            {entry.is_primary && (
                              <Crown className="w-8 h-8 text-[#FFDD59]" />
                            )}
                            <div>
                              <div className="text-white font-bold text-lg flex items-center gap-3">
                                {entry.email}
                                {entry.is_primary && (
                                  <span className="text-sm bg-[#FFDD59] text-black px-3 py-1 rounded-full font-bold">
                                    ANA YETKİLİ
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-400 mt-1">
                                Ekleyen: {entry.added_by} • Tarih: {entry.added_at ? new Date(entry.added_at).toLocaleDateString('tr-TR') : '-'}
                              </div>
                            </div>
                          </div>
                          
                          {!entry.is_primary && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRemoveFromWhitelist(entry.email)}
                              disabled={removingWhitelistEmail === entry.email}
                              className="border-red-500 text-red-400 hover:bg-red-900/30 hover:text-red-300"
                            >
                              {removingWhitelistEmail === entry.email ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Çıkar
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Info Note */}
              <div className="p-5 bg-gray-800 rounded-lg border-2 border-gray-600">
                <p className="text-base text-gray-300">
                  <span className="text-[#FFDD59] font-bold">🔒 Güvenlik Notu:</span> Sadece 2 admin vardır: 
                  <span className="text-[#FFDD59] font-semibold"> bora@personaajans.com</span> ve 
                  <span className="text-[#FFDD59] font-semibold"> orkun@personaajans.com</span>. 
                  Diğer @personaajans.com uzantılı kullanıcılar <span className="text-white font-semibold">Persona ekip üyesi</span> olarak kaydedilir. 
                  Admin ve ekip üyeleri <span className="text-green-400 font-semibold">sınırsız analiz hakkı</span> alırlar. 
                  Adminler istedikleri ekip üyesini silebilir ve herhangi bir kullanıcıya hediye kredi tanımlayabilir. 
                  Ana yetkili adminler listeden çıkarılamaz.
                </p>
              </div>
            </div>
          )}

          {/* DASHBOARD TAB */}
          {activeTab === "dashboard" && (
          <>
          {/* Stats */}
          {analyticsLoading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#FFDD59]" />
            </div>
          ) : analytics ? (
            <>
              {/* Main Metrics - Top Row */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <Card 
                  className="bg-gradient-to-br from-blue-900 to-blue-800 border-blue-700 cursor-pointer hover:scale-105 transition-transform"
                  onClick={() => document.getElementById('users-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm text-blue-200">Toplam Kullanıcı</div>
                      <Users className="w-5 h-5 text-blue-300" />
                    </div>
                    <div className="text-4xl font-bold text-white mb-1">{analytics.total_users}</div>
                    <div className="text-xs text-blue-200">
                      +{analytics.new_users_today} bugün, +{analytics.new_users_week} bu hafta
                    </div>
                  </CardContent>
                </Card>

                <Card 
                  className="bg-gradient-to-br from-green-900 to-green-800 border-green-700 cursor-pointer hover:scale-105 transition-transform"
                  onClick={() => document.getElementById('revenue-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm text-green-200">Toplam Gelir</div>
                      <DollarSign className="w-5 h-5 text-green-300" />
                    </div>
                    <div className="text-4xl font-bold text-white mb-1">
                      ₺{analytics.total_revenue.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    </div>
                    <div className="text-xs text-green-200">
                      {Object.keys(analytics.revenue_by_plan).length} farklı plan
                    </div>
                  </CardContent>
                </Card>

                <Card 
                  className="bg-gradient-to-br from-purple-900 to-purple-800 border-purple-700 cursor-pointer hover:scale-105 transition-transform"
                  onClick={() => document.getElementById('queries-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm text-purple-200">Toplam Sorgu</div>
                      <Activity className="w-5 h-5 text-purple-300" />
                    </div>
                    <div className="text-4xl font-bold text-white mb-1">{analytics.total_queries}</div>
                    <div className="text-xs text-purple-200">
                      {analytics.queries_today} bugün, {analytics.queries_week} bu hafta
                    </div>
                  </CardContent>
                </Card>

                <Card 
                  className="bg-gradient-to-br from-orange-900 to-orange-800 border-orange-700 cursor-pointer hover:scale-105 transition-transform"
                  onClick={() => document.getElementById('queries-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm text-orange-200">Aylık Sorgu</div>
                      <TrendingUp className="w-5 h-5 text-orange-300" />
                    </div>
                    <div className="text-4xl font-bold text-white mb-1">{analytics.queries_month}</div>
                    <div className="text-xs text-orange-200">
                      Son 30 gün
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Detailed Analytics - Second Row */}
              <div id="revenue-section" className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* Active Subscriptions Breakdown */}
                <Card className="bg-gray-900 border-gray-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-[#FFDD59]" />
                      Aktif Abonelikler
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(analytics.active_subscriptions).map(([planType, count]) => (
                        <div key={planType} className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                          <div>
                            <div className={`font-bold ${
                              planType === 'free' ? 'text-blue-400' :
                              planType === 'persona_staff' ? 'text-purple-400' :
                              planType === 'gift' ? 'text-yellow-400' :
                              planType.includes('pro') ? 'text-green-400' :
                              planType.includes('business') ? 'text-red-400' :
                              'text-gray-400'
                            }`}>
                              {planType.toUpperCase()}
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              {((count / analytics.total_users) * 100).toFixed(1)}% of total
                            </div>
                          </div>
                          <div className="text-2xl font-bold text-white">{count}</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Revenue by Plan */}
                <Card className="bg-gray-900 border-gray-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-green-400" />
                      Plan Bazlı Gelir
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(analytics.revenue_by_plan)
                        .sort(([,a], [,b]) => b - a)
                        .map(([planName, amount]) => (
                        <div key={planName} className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                          <div>
                            <div className="font-bold text-green-400">
                              {planName}
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              {((amount / analytics.total_revenue) * 100).toFixed(1)}% of total
                            </div>
                          </div>
                          <div className="text-xl font-bold text-white">
                            ₺{amount.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </div>
                        </div>
                      ))}
                      {Object.keys(analytics.revenue_by_plan).length === 0 && (
                        <div className="text-center text-gray-500 py-4">
                          Henüz gelir kaydı yok
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* User Growth Statistics */}
              <Card className="bg-gray-900 border-gray-800 mb-8">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-400" />
                    Kullanıcı Büyümesi
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-gray-800 rounded-lg">
                      <div className="text-sm text-gray-400 mb-2">Bugün Kayıt Olan</div>
                      <div className="text-3xl font-bold text-blue-400">{analytics.new_users_today}</div>
                    </div>
                    <div className="p-4 bg-gray-800 rounded-lg">
                      <div className="text-sm text-gray-400 mb-2">Son 7 Gün</div>
                      <div className="text-3xl font-bold text-green-400">{analytics.new_users_week}</div>
                    </div>
                    <div className="p-4 bg-gray-800 rounded-lg">
                      <div className="text-sm text-gray-400 mb-2">Son 30 Gün</div>
                      <div className="text-3xl font-bold text-purple-400">{analytics.new_users_month}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Conversion Rates */}
              {conversionRates && (
                <Card className="bg-gray-900 border-gray-800 mb-8">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-green-400" />
                      Dönüşüm Oranları & Ortalamalar
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-gray-800 rounded-lg">
                        <div className="text-sm text-gray-400 mb-2">Dönüşüm Oranı</div>
                        <div className="text-2xl font-bold text-green-400">{conversionRates.conversion_rate}%</div>
                        <div className="text-xs text-gray-500 mt-1">{conversionRates.paid_users} / {conversionRates.total_users} ücretli</div>
                      </div>
                      <div className="p-4 bg-gray-800 rounded-lg">
                        <div className="text-sm text-gray-400 mb-2">Kul. Başına Ort. Gelir</div>
                        <div className="text-2xl font-bold text-green-400">₺{conversionRates.avg_revenue_per_user.toFixed(2)}</div>
                        <div className="text-xs text-gray-500 mt-1">Ücretli kullanıcı başına</div>
                      </div>
                      <div className="p-4 bg-gray-800 rounded-lg">
                        <div className="text-sm text-gray-400 mb-2">Kul. Başına Ort. Sorgu</div>
                        <div className="text-2xl font-bold text-purple-400">{conversionRates.avg_queries_per_user.toFixed(1)}</div>
                        <div className="text-xs text-gray-500 mt-1">Tüm kullanıcılar</div>
                      </div>
                      <div className="p-4 bg-gray-800 rounded-lg">
                        <div className="text-sm text-gray-400 mb-2">Toplam Sorgu</div>
                        <div className="text-2xl font-bold text-blue-400">{conversionRates.total_queries}</div>
                        <div className="text-xs text-gray-500 mt-1">Tüm zamanlar</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Data Visualizations Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* Subscription Distribution Pie Chart */}
                <Card className="bg-gray-900 border-gray-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-[#FFDD59]" />
                      Plan Dağılımı
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={Object.entries(analytics.active_subscriptions).map(([name, value]) => ({
                            name: name.toUpperCase(),
                            value: value
                          }))}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {Object.keys(analytics.active_subscriptions).map((entry, index) => {
                            const colors = {
                              'free': '#3B82F6',
                              'persona_staff': '#A855F7',
                              'gift': '#FFDD59',
                              'pro_monthly': '#10B981',
                              'business_monthly': '#EF4444'
                            };
                            return <Cell key={`cell-${index}`} fill={colors[entry] || '#6B7280'} />;
                          })}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }}
                          labelStyle={{ color: '#F3F4F6' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Query Activity Bar Chart */}
                <Card className="bg-gray-900 border-gray-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Activity className="w-5 h-5 text-purple-400" />
                      Plan Bazlı Sorgu Aktivitesi
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={Object.entries(analytics.queries_by_plan || {}).map(([name, value]) => ({
                          name: name.toUpperCase(),
                          queries: value
                        }))}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis 
                          dataKey="name" 
                          stroke="#9CA3AF" 
                          tick={{ fill: '#9CA3AF', fontSize: 12 }}
                          angle={-45}
                          textAnchor="end"
                          height={80}
                        />
                        <YAxis stroke="#9CA3AF" tick={{ fill: '#9CA3AF' }} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }}
                          labelStyle={{ color: '#F3F4F6' }}
                        />
                        <Bar dataKey="queries" fill="#A855F7" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              {/* Top Influencers Bar Chart */}
              <Card className="bg-gray-900 border-gray-800 mb-8">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[#FFDD59]" />
                    En Çok Sorgulanan Influencer'lar (Top 10)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                      data={topInfluencers.map(inf => ({
                        name: `@${inf.username}`,
                        queries: inf.query_count
                      }))}
                      layout="horizontal"
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis type="number" stroke="#9CA3AF" tick={{ fill: '#9CA3AF' }} />
                      <YAxis 
                        type="category" 
                        dataKey="name" 
                        stroke="#9CA3AF" 
                        tick={{ fill: '#9CA3AF', fontSize: 12 }}
                        width={150}
                      />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }}
                        labelStyle={{ color: '#F3F4F6' }}
                      />
                      <Bar dataKey="queries" fill="#FFDD59" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Revenue Distribution */}
              {Object.keys(analytics.revenue_by_plan).length > 0 && (
                <Card className="bg-gray-900 border-gray-800 mb-8">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-green-400" />
                      Gelir Dağılımı (Plan Bazlı)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={Object.entries(analytics.revenue_by_plan)
                            .sort(([,a], [,b]) => b - a)
                            .map(([name, value]) => ({
                              name: name,
                              value: value
                            }))}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, value, percent }) => `${name}: ₺${value.toFixed(0)} (${(percent * 100).toFixed(0)}%)`}
                          outerRadius={100}
                          fill="#10B981"
                          dataKey="value"
                        >
                          {Object.keys(analytics.revenue_by_plan).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={['#10B981', '#34D399', '#6EE7B7', '#A7F3D0'][index % 4]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }}
                          labelStyle={{ color: '#F3F4F6' }}
                          formatter={(value) => `₺${value.toFixed(2)}`}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Top Influencers & Active Users Side by Side */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* Top Queried Influencers */}
                <Card className="bg-gray-900 border-gray-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-[#FFDD59]" />
                      En Çok Sorgulanan Influencer'lar
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {topInfluencers.length === 0 ? (
                        <div className="text-center text-gray-500 py-4">Henüz sorgu yok</div>
                      ) : (
                        topInfluencers.map((influencer, index) => (
                          <div key={influencer.username} className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="text-2xl font-bold text-[#FFDD59]">#{index + 1}</div>
                              <div>
                                <div className="font-medium text-white">@{influencer.username}</div>
                                <div className="text-xs text-gray-400">
                                  {new Date(influencer.last_queried).toLocaleDateString('tr-TR')}
                                </div>
                              </div>
                            </div>
                            <div className="text-xl font-bold text-white">{influencer.query_count}</div>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Most Active Users */}
                <Card className="bg-gray-900 border-gray-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Users className="w-5 h-5 text-blue-400" />
                      En Aktif Kullanıcılar
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {activeUsers.length === 0 ? (
                        <div className="text-center text-gray-500 py-4">Henüz aktif kullanıcı yok</div>
                      ) : (
                        activeUsers.map((user, index) => (
                          <div key={user.email} className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="text-lg font-bold text-blue-400">#{index + 1}</div>
                              <div>
                                <div className="font-medium text-white text-sm">{user.first_name} {user.last_name}</div>
                                <div className="text-xs text-gray-400">{user.email}</div>
                                <div className="text-xs text-green-400 mt-0.5">{user.plan_type} • {user.credits_remaining} analiz hakkı</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-bold text-white">{user.query_count}</div>
                              <div className="text-xs text-gray-400">sorgu</div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <Card className="bg-gray-900 border-gray-800">
                <CardContent className="p-4">
                  <div className="text-sm text-gray-400">Toplam Kullanıcı</div>
                  <div className="text-3xl font-bold text-[#FFDD59]">{users.length}</div>
                </CardContent>
              </Card>
              <Card className="bg-gray-900 border-gray-800">
                <CardContent className="p-4">
                  <div className="text-sm text-gray-400">Free Plan</div>
                  <div className="text-3xl font-bold text-blue-500">
                    {users.filter(u => u.plan_type === 'free').length}
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gray-900 border-gray-800">
                <CardContent className="p-4">
                  <div className="text-sm text-gray-400">Ücretli Plan</div>
                  <div className="text-3xl font-bold text-green-500">
                    {users.filter(u => ['light', 'pro', 'business'].includes(u.plan_type)).length}
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gray-900 border-gray-800">
                <CardContent className="p-4">
                  <div className="text-sm text-gray-400">Persona Çalışan</div>
                  <div className="text-3xl font-bold text-purple-500">
                    {users.filter(u => u.plan_type === 'persona_staff').length}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Export Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Button
              onClick={handleExportUsers}
              className="bg-green-600 hover:bg-green-700 text-white font-bold py-6 text-lg"
            >
              <Download className="w-5 h-5 mr-2" />
              Üye Listesini Excel'e Aktar
            </Button>
            <Button
              onClick={handleExportQueries}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-6 text-lg"
            >
              <Download className="w-5 h-5 mr-2" />
              Sorgu Geçmişini Excel'e Aktar
            </Button>
            <Button
              onClick={() => setShowGiftModal(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-6 text-lg"
            >
              <Gift className="w-5 h-5 mr-2" />
              Hediye Sorgu Oluştur
            </Button>
          </div>

          {/* Users Table */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle id="users-section" className="text-white">Tüm Kullanıcılar</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-800 text-gray-400">
                      <th className="text-left p-3">Email</th>
                      <th className="text-left p-3">İsim</th>
                      <th className="text-left p-3">Profil Tipi</th>
                      <th className="text-left p-3">Plan</th>
                      <th className="text-left p-3">Durum</th>
                      <th className="text-left p-3">Kalan Sorgu</th>
                      <th className="text-left p-3">Kayıt Tarihi</th>
                      <th className="text-left p-3">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id} className="border-b border-gray-800 text-gray-300 hover:bg-gray-800/50">
                        <td className="p-3 text-sm">{user.email}</td>
                        <td className="p-3">{user.first_name} {user.last_name}</td>
                        <td className="p-3">
                          <span className="px-2 py-1 rounded text-xs bg-gray-800">
                            {user.profile_type}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded text-xs font-bold ${
                            user.plan_type === 'free' ? 'bg-blue-900 text-blue-300' :
                            user.plan_type === 'persona_staff' ? 'bg-purple-900 text-purple-300' :
                            user.plan_type === 'gift' ? 'bg-yellow-900 text-yellow-300' :
                            'bg-green-900 text-green-300'
                          }`}>
                            {user.plan_type}
                          </span>
                        </td>
                        <td className="p-3">
                          {/* DURUM rozeti: backend filtresi gevsedi (find({})) -> dogrulanmamis
                              uyeler de listede. Aktif / Dogrulanmamis ayrimini rozetle koru. */}
                          <span className={`px-2 py-1 rounded text-xs font-bold ${
                            user.active ? 'bg-green-900 text-green-300' : 'bg-orange-900 text-orange-300'
                          }`}>
                            {user.active ? 'Aktif' : 'Doğrulanmamış'}
                          </span>
                        </td>
                        <td className="p-3 font-bold">{user.credits_remaining}/{user.credits_limit}</td>
                        <td className="p-3 text-xs">
                          {new Date(user.created_at).toLocaleDateString('tr-TR')}
                        </td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <Button
                              onClick={() => openCreditModal(user)}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white"
                              title="Kredi Ekle"
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                            {/* v31.x: - (kredi dusur) butonu DEVRE-DISI. Kredi dusurme/dondurma
                                freeze/unfreeze isine devredildi (handbook to-do). Buraya freeze toggle gelecek. */}
                            {["pro_monthly", "business_monthly"].includes(user.plan_type) && (
                              <Button
                                onClick={() => handleCancelSubscription(user.id, user.email)}
                                size="sm"
                                className="bg-red-600 hover:bg-red-700 text-white"
                                title="Üyeliği İptal Et"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              onClick={() => openDeleteModal(user)}
                              size="sm"
                              className="bg-gray-700 hover:bg-gray-800 text-red-400 hover:text-red-300"
                              title="Kullanıcıyı Sil"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          </>
          )}

          {/* USER STATS TAB - OPARA v28.1 */}
          {activeTab === "userstats" && (
            <div className="space-y-6">
              {/* Date Range Picker */}
              <Card className="bg-gray-900/50 border-blue-500/30">
                <CardContent className="p-6">
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-blue-400" />
                      <span className="text-gray-300 font-medium">Tarih Aralığı:</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        value={statsDateRange.from}
                        onChange={(e) => handleDateRangeChange('from', e.target.value)}
                        className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                      />
                      <span className="text-gray-400">-</span>
                      <input
                        type="date"
                        value={statsDateRange.to}
                        onChange={(e) => handleDateRangeChange('to', e.target.value)}
                        className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                    <Button
                      onClick={() => fetchUserStats(adminToken, statsDateRange.from, statsDateRange.to)}
                      disabled={userStatsLoading}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {userStatsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      <span className="ml-2">Yenile</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Summary Stats */}
              {userStats && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="bg-gradient-to-br from-blue-900/50 to-blue-800/50 border-blue-700">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm text-blue-200">Aktif Kullanıcı</div>
                        <Users className="w-5 h-5 text-blue-300" />
                      </div>
                      <div className="text-4xl font-bold text-white">{userStats.summary?.active_users || 0}</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-green-900/50 to-green-800/50 border-green-700">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm text-green-200">Toplam Giriş</div>
                        <LogIn className="w-5 h-5 text-green-300" />
                      </div>
                      <div className="text-4xl font-bold text-white">{userStats.summary?.total_logins || 0}</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-purple-900/50 to-purple-800/50 border-purple-700">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm text-purple-200">Toplam Analiz</div>
                        <Activity className="w-5 h-5 text-purple-300" />
                      </div>
                      <div className="text-4xl font-bold text-white">{userStats.summary?.total_analyses || 0}</div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* User Table */}
              <Card className="bg-gray-900/50 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-blue-400" />
                    Kullanıcı Aktiviteleri
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {userStatsLoading ? (
                    <div className="flex justify-center items-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
                    </div>
                  ) : !userStats || userStats.users?.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                      <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Seçili tarih aralığında veri bulunamadı</p>
                      <p className="text-sm mt-2">Farklı bir tarih aralığı seçmeyi deneyin</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-700">
                            <th className="text-left py-3 px-4 text-gray-400 font-medium">Kullanıcı</th>
                            <th className="text-left py-3 px-4 text-gray-400 font-medium">Plan</th>
                            <th className="text-center py-3 px-4 text-gray-400 font-medium">Giriş Sayısı</th>
                            <th className="text-center py-3 px-4 text-gray-400 font-medium">Analiz Sayısı</th>
                            <th className="text-left py-3 px-4 text-gray-400 font-medium">Son Giriş</th>
                            <th className="text-center py-3 px-4 text-gray-400 font-medium">Detay</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userStats.users.map((user, index) => (
                            <tr key={user.user_id || index} className="border-b border-gray-800 hover:bg-gray-800/50">
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm">
                                    {user.email?.[0]?.toUpperCase() || '?'}
                                  </div>
                                  <div>
                                    <div className="text-white font-medium">{user.full_name || user.email?.split('@')[0] || 'N/A'}</div>
                                    <div className="text-gray-500 text-sm">{user.email}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  user.plan_type === 'persona_staff' ? 'bg-purple-500/20 text-purple-400' :
                                  user.plan_type === 'pro_monthly' ? 'bg-blue-500/20 text-blue-400' :
                                  user.plan_type === 'business_monthly' ? 'bg-green-500/20 text-green-400' :
                                  'bg-gray-500/20 text-gray-400'
                                }`}>
                                  {user.plan_type || 'free'}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-center">
                                <span className="text-green-400 font-bold">{user.login_count}</span>
                              </td>
                              <td className="py-3 px-4 text-center">
                                <span className="text-purple-400 font-bold">{user.analysis_count}</span>
                              </td>
                              <td className="py-3 px-4 text-gray-400 text-sm">
                                {user.last_login ? new Date(user.last_login).toLocaleString('tr-TR') : '-'}
                              </td>
                              <td className="py-3 px-4 text-center">
                                {user.analyzed_influencers?.length > 0 ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleShowUserDetail(user)}
                                    className="border-blue-500 text-blue-400 hover:bg-blue-500/20"
                                  >
                                    <Eye className="w-4 h-4 mr-1" />
                                    {user.analyzed_influencers.length} Analiz
                                  </Button>
                                ) : (
                                  <span className="text-gray-600">-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* User Detail Modal */}
          {showDetailModal && selectedUserDetail && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
              <Card className="w-full max-w-2xl bg-gray-900 border-blue-500 max-h-[80vh] overflow-hidden flex flex-col">
                <CardHeader className="border-b border-gray-700">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Users className="w-5 h-5 text-blue-400" />
                      {selectedUserDetail.full_name || selectedUserDetail.email}
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowDetailModal(false)}
                      className="text-gray-400 hover:text-white"
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                  <p className="text-gray-400 text-sm">{selectedUserDetail.email}</p>
                </CardHeader>
                <CardContent className="p-6 overflow-y-auto">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-purple-400" />
                    Analiz Edilen Influencer'lar ({selectedUserDetail.analyzed_influencers?.length || 0})
                  </h3>
                  {selectedUserDetail.analyzed_influencers?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedUserDetail.analyzed_influencers.map((inf, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-gray-800 rounded-lg p-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                              {inf.username?.[1]?.toUpperCase() || '?'}
                            </div>
                            <div>
                              <div className="text-white font-medium">{inf.username}</div>
                              <div className="text-gray-500 text-sm">{inf.time || 'N/A'}</div>
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            inf.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                            inf.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                            inf.status === 'processing' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {inf.status === 'completed' ? 'Tamamlandı' :
                             inf.status === 'failed' ? 'Başarısız' :
                             inf.status === 'processing' ? 'İşleniyor' : inf.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">Bu tarih aralığında analiz bulunamadı</p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* REELS SNAPSHOT TAB - OPARA v30.0 */}
          {activeTab === "reels" && (
            <div className="space-y-6">
              {/* OPARA v30.0: Tarih Filtresi */}
              <Card className="bg-gray-900 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-yellow-400" />
                    Tarih Filtresi
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Belirli bir zaman diliminde yapılan analizleri listeleyin
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Hızlı Filtreler */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {[
                      { key: 'today', label: 'Bugün' },
                      { key: 'yesterday', label: 'Dün' },
                      { key: '7days', label: 'Son 7 Gün' },
                      { key: '15days', label: 'Son 15 Gün' },
                      { key: '30days', label: 'Son 30 Gün' },
                      { key: '90days', label: 'Son 90 Gün' },
                    ].map((filter) => (
                      <Button
                        key={filter.key}
                        onClick={() => handleDateFilterChange(filter.key)}
                        variant={dateFilter === filter.key ? "default" : "outline"}
                        className={`${
                          dateFilter === filter.key
                            ? 'bg-yellow-500 text-black hover:bg-yellow-600'
                            : 'border-gray-600 text-gray-300 hover:bg-gray-700'
                        }`}
                      >
                        {filter.label}
                      </Button>
                    ))}
                  </div>

                  {/* Custom Tarih Aralığı */}
                  <div className="flex flex-wrap items-end gap-3 p-4 bg-gray-800 rounded-lg">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Başlangıç Tarihi</label>
                      <Input
                        type="date"
                        value={customDateRange.from}
                        onChange={(e) => setCustomDateRange(prev => ({ ...prev, from: e.target.value }))}
                        className="bg-gray-700 border-gray-600 text-white w-40"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Bitiş Tarihi</label>
                      <Input
                        type="date"
                        value={customDateRange.to}
                        onChange={(e) => setCustomDateRange(prev => ({ ...prev, to: e.target.value }))}
                        className="bg-gray-700 border-gray-600 text-white w-40"
                      />
                    </div>
                    <Button
                      onClick={handleCustomDateSearch}
                      className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold"
                    >
                      <Search className="w-4 h-4 mr-2" />
                      Ara
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Analiz Listesi - v30.19: Sıralama değişti - önce Analiz Geçmişi */}
              <Card className="bg-gray-900 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Activity className="w-5 h-5 text-yellow-400" />
                    Analiz Geçmişi
                    {analysisHistory.length > 0 && (
                      <span className="text-sm bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded-full">
                        {analysisHistory.length} sonuç
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {historyLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-yellow-400" />
                    </div>
                  ) : analysisHistory.length > 0 ? (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {analysisHistory.map((analysis, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-3 bg-gray-800 rounded-lg hover:bg-gray-700 cursor-pointer transition-colors"
                          onClick={() => {
                            setReelsSnapshotSearchInput(analysis.username);
                            setReelsSnapshotUsername(analysis.username);
                            setShowReelsSnapshot(true);
                          }}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-yellow-500/20 rounded-full flex items-center justify-center">
                              <span className="text-yellow-400 font-bold text-sm">{idx + 1}</span>
                            </div>
                            <div>
                              <div className="text-white font-medium flex items-center gap-2">
                                @{analysis.username}
                                {analysis.status === 'failed' && (
                                  <span className="px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 text-xs font-semibold">
                                    FAIL{analysis.error_type ? ` · ${analysis.error_type}` : ''}
                                  </span>
                                )}
                                {analysis.status === 'completed' && (
                                  <span className="px-1.5 py-0.5 rounded bg-green-500/20 text-green-400 text-xs">OK</span>
                                )}
                              </div>
                              <div className="text-gray-400 text-sm">
                                {analysis.query_time ? new Date(analysis.query_time).toLocaleString('tr-TR') : '-'}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {analysis.user_email && (
                              <span className="text-gray-400 text-sm">{analysis.user_email}</span>
                            )}
                            <Button
                              size="sm"
                              className="bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30"
                              onClick={(e) => {
                                e.stopPropagation();
                                setReelsSnapshotUsername(analysis.username);
                                setShowReelsSnapshot(true);
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : dateFilter ? (
                    <p className="text-gray-500 text-center py-8">Bu tarih aralığında analiz bulunamadı</p>
                  ) : (
                    <p className="text-gray-500 text-center py-8">Analiz listesi için yukarıdan bir tarih filtresi seçin</p>
                  )}
                </CardContent>
              </Card>

              {/* OPARA v30.7: Son 10 Analiz - v30.19: Sıralamada 3. sıra */}
              <Card className="bg-gray-900 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Clock className="w-5 h-5 text-blue-400" />
                    Son 10 Analiz
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Sistemdeki en son 10 analiz (aynı influencer birden fazla olabilir)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {last10Loading ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="w-6 h-6 animate-spin text-yellow-400" />
                    </div>
                  ) : last10Analyses.length === 0 ? (
                    <p className="text-gray-400 text-center py-4">Henüz analiz yok</p>
                  ) : (
                    <div className="space-y-2">
                      {last10Analyses.map((analysis, idx) => (
                        <div
                          key={idx}
                          className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                            analysis.status === 'FAILED' 
                              ? 'bg-red-900/30 hover:bg-red-900/50 border border-red-500/30' 
                              : 'bg-gray-800 hover:bg-gray-700'
                          }`}
                          onClick={() => {
                            if (analysis.status !== 'FAILED') {
                              setReelsSnapshotUsername(analysis.username);
                              setShowReelsSnapshot(true);
                            }
                          }}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
                              analysis.status === 'FAILED' ? 'bg-red-500/20' : 'bg-blue-500/20'
                            }`}>
                              <span className={`font-bold text-xs ${
                                analysis.status === 'FAILED' ? 'text-red-400' : 'text-blue-400'
                              }`}>{idx + 1}</span>
                            </div>
                            <div>
                              <div className={`font-medium ${
                                analysis.status === 'FAILED' ? 'text-red-300' : 'text-white'
                              }`}>@{analysis.username}</div>
                              <div className="text-gray-500 text-xs">
                                {analysis.query_time ? new Date(analysis.query_time).toLocaleString('tr-TR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                }) : '-'}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            {analysis.status === 'FAILED' ? (
                              <span className="text-red-400 font-bold text-sm">
                                Başarısız
                              </span>
                            ) : analysis.analysis_duration ? (
                              <span className="text-yellow-400 font-mono text-sm">
                                {Math.floor(analysis.analysis_duration / 60) > 0 
                                  ? `${Math.floor(analysis.analysis_duration / 60)} dk ${Math.round(analysis.analysis_duration % 60)} sn`
                                  : `${Math.round(analysis.analysis_duration)} sn`
                                }
                              </span>
                            ) : (
                              <span className="text-gray-500 text-sm">-</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Manuel Arama */}
              <Card className="bg-gray-900 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Search className="w-5 h-5 text-yellow-400" />
                    İnfluencer Ara
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Analizi yapılmış bir influencer'ın kullanıcı adını girin
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-3">
                    <Input
                      type="text"
                      placeholder="örn: cemalcancanseven"
                      value={reelsSnapshotSearchInput}
                      onChange={(e) => setReelsSnapshotSearchInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && reelsSnapshotSearchInput.trim()) {
                          setReelsSnapshotUsername(reelsSnapshotSearchInput.trim().toLowerCase().replace('@', ''));
                          setShowReelsSnapshot(true);
                        }
                      }}
                      className="bg-gray-800 border-gray-700 text-white flex-1"
                    />
                    <Button
                      onClick={() => {
                        if (reelsSnapshotSearchInput.trim()) {
                          setReelsSnapshotUsername(reelsSnapshotSearchInput.trim().toLowerCase().replace('@', ''));
                          setShowReelsSnapshot(true);
                        } else {
                          toast.error("Lütfen bir kullanıcı adı girin");
                        }
                      }}
                      disabled={reelsSnapshotLoading || !reelsSnapshotSearchInput.trim()}
                      className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold px-6"
                    >
                      {reelsSnapshotLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Eye className="w-4 h-4 mr-2" />
                          Görüntüle
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-gray-500 text-sm mt-3">
                    Not: Sadece v28.0 ve sonrasında yapılan analizler detaylı veri tablosu içerir.
                  </p>
                </CardContent>
              </Card>

              {/* OPARA v30.6: Kullanıcı Ara */}
              <Card className="bg-gray-900 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Users className="w-5 h-5 text-yellow-400" />
                    Kullanıcı Ara
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Kayıtlı bir kullanıcının email adresini girerek yaptığı analizleri görün
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-3">
                    <Input
                      type="email"
                      placeholder="örn: user@example.com"
                      value={userSearchEmail}
                      onChange={(e) => setUserSearchEmail(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && userSearchEmail.trim()) {
                          handleUserSearch();
                        }
                      }}
                      className="bg-gray-800 border-gray-700 text-white flex-1"
                    />
                    <Button
                      onClick={handleUserSearch}
                      disabled={userSearchLoading || !userSearchEmail.trim()}
                      className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold px-6"
                    >
                      {userSearchLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Search className="w-4 h-4 mr-2" />
                          Ara
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {/* Kullanıcı Arama Sonuçları */}
                  {userSearchResults.length > 0 && (
                    <div className="mt-4">
                      <div className="text-sm text-gray-400 mb-2">
                        {userSearchResults.length} analiz bulundu
                      </div>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {userSearchResults.map((analysis, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between p-3 bg-gray-800 rounded-lg hover:bg-gray-700 cursor-pointer transition-colors"
                            onClick={() => {
                              setReelsSnapshotUsername(analysis.username);
                              setShowReelsSnapshot(true);
                            }}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                                <span className="text-blue-400 font-bold text-sm">{idx + 1}</span>
                              </div>
                              <div>
                                <div className="text-white font-medium">@{analysis.username}</div>
                                <div className="text-gray-400 text-sm">
                                  {analysis.query_time ? new Date(analysis.query_time).toLocaleString('tr-TR') : '-'}
                                </div>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              className="bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30"
                              onClick={(e) => {
                                e.stopPropagation();
                                setReelsSnapshotUsername(analysis.username);
                                setShowReelsSnapshot(true);
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Info Header - En altta */}
              <div className="bg-yellow-500/10 border-2 border-yellow-500 rounded-lg p-6">
                <h2 className="text-2xl font-bold text-yellow-400 mb-2">
                  <Table2 className="w-6 h-6 inline mr-2" />
                  Reels Snapshot - Detaylı Veri Tablosu
                </h2>
                <p className="text-gray-300">
                  İnfluencer analizi yapıldığında kaydedilen <span className="text-yellow-400 font-bold">ilk 12 reel'in</span> detaylı metriklerini görüntüleyin.
                  Shortcode, Tarih, View, Like, Comment, Pinned ve Marka bilgileri.
                </p>
              </div>
            </div>
          )}

          {/* Gift Modal */}
          {showGiftModal && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
              <Card className="w-full max-w-md bg-gray-900 border-purple-500">
                <CardHeader>
                  <CardTitle className="text-purple-400 flex items-center gap-2">
                    <Gift className="w-6 h-6" />
                    Hediye Sorgu Hakkı Oluştur
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleGiftQuota} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Email
                      </label>
                      <Input
                        type="email"
                        value={giftEmail}
                        onChange={(e) => setGiftEmail(e.target.value)}
                        placeholder="[email protected]"
                        required
                        className="bg-gray-800 border-gray-700 text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Şifre
                      </label>
                      <Input
                        type="text"
                        value={giftPassword}
                        onChange={(e) => setGiftPassword(e.target.value)}
                        placeholder="Şifre belirleyin"
                        required
                        className="bg-gray-800 border-gray-700 text-white"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          İsim
                        </label>
                        <Input
                          type="text"
                          value={giftFirstName}
                          onChange={(e) => setGiftFirstName(e.target.value)}
                          placeholder="İsim"
                          required
                          className="bg-gray-800 border-gray-700 text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Soyisim
                        </label>
                        <Input
                          type="text"
                          value={giftLastName}
                          onChange={(e) => setGiftLastName(e.target.value)}
                          placeholder="Soyisim"
                          required
                          className="bg-gray-800 border-gray-700 text-white"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Sorgu Hakkı
                        </label>
                        <Input
                          type="number"
                          value={giftQuota}
                          onChange={(e) => setGiftQuota(e.target.value)}
                          placeholder="10"
                          required
                          min="1"
                          className="bg-gray-800 border-gray-700 text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Geçerlilik (Gün)
                        </label>
                        <Input
                          type="number"
                          value={giftDuration}
                          onChange={(e) => setGiftDuration(e.target.value)}
                          placeholder="30"
                          required
                          min="1"
                          className="bg-gray-800 border-gray-700 text-white"
                        />
                      </div>
                    </div>

                    <div className="flex gap-3 mt-6">
                      <Button
                        type="submit"
                        disabled={giftLoading}
                        className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-bold"
                      >
                        {giftLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Oluşturuluyor...
                          </>
                        ) : (
                          "Oluştur"
                        )}
                      </Button>
                      <Button
                        type="button"
                        onClick={() => setShowGiftModal(false)}
                        variant="outline"
                        className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-800"
                      >
                        İptal
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Delete User Confirmation Modal */}
          {showDeleteModal && userToDelete && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
              <Card className="w-full max-w-md bg-gray-900 border-red-500">
                <CardHeader>
                  <CardTitle className="text-red-400 flex items-center gap-2">
                    <Trash2 className="w-6 h-6" />
                    Kullanıcıyı Sil
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4">
                      <p className="text-white font-semibold mb-2">
                        Bu işlem geri alınamaz!
                      </p>
                      <p className="text-gray-300 text-sm">
                        Aşağıdaki kullanıcı ve tüm verileri (sorgular, raporlar) kalıcı olarak silinecektir:
                      </p>
                      <div className="mt-3 bg-gray-800 rounded p-3">
                        <p className="text-white font-mono">{userToDelete.email}</p>
                        <p className="text-gray-400 text-sm">{userToDelete.first_name} {userToDelete.last_name}</p>
                        <p className="text-gray-500 text-xs mt-1">Plan: {userToDelete.plan_type}</p>
                      </div>
                      {/* Aktif abonelik uyarisi: /admin/users yaniti stripe_subscription_id icermez ->
                          plan_type proxy'si (X butonuyla ayni sinyal). Backend has_active_subscription otoritedir. */}
                      {["pro_monthly", "business_monthly"].includes(userToDelete.plan_type) && (
                        <p className="text-amber-300 text-sm mt-3">
                          This user has an active subscription. It will be cancelled immediately in Stripe.
                        </p>
                      )}
                    </div>
                    
                    <div className="flex gap-3 mt-6">
                      <Button
                        onClick={handleDeleteUser}
                        disabled={deleteLoading}
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold"
                      >
                        {deleteLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Siliniyor...
                          </>
                        ) : (
                          <>
                            <Trash2 className="mr-2 h-4 w-4" />
                            Evet, Sil
                          </>
                        )}
                      </Button>
                      <Button
                        type="button"
                        onClick={() => {
                          setShowDeleteModal(false);
                          setUserToDelete(null);
                        }}
                        variant="outline"
                        disabled={deleteLoading}
                        className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-800"
                      >
                        İptal
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Admin +Kredi: miktar-secim modali (10/50/100 preset + serbest giris) */}
          {showCreditModal && creditUser && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
              <Card className="w-full max-w-md bg-gray-900 border-green-500">
                <CardHeader>
                  <CardTitle className="text-green-400 flex items-center gap-2">
                    <Plus className="w-6 h-6" />
                    Kredi Ekle
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="bg-gray-800 rounded p-3">
                      <p className="text-white font-mono">{creditUser.email}</p>
                      <p className="text-gray-400 text-sm">Mevcut: {creditUser.credits_remaining} kredi</p>
                    </div>

                    <p className="text-gray-300 text-sm">Kaç kredi eklensin?</p>

                    {/* Hazir miktar butonlari */}
                    <div className="flex gap-2">
                      {[10, 50, 100].map((amt) => (
                        <Button
                          key={amt}
                          type="button"
                          onClick={() => setCreditAmount(String(amt))}
                          className={`flex-1 ${parseInt(creditAmount) === amt ? 'bg-green-600 text-white' : 'bg-gray-700 hover:bg-gray-600 text-gray-200'}`}
                        >
                          +{amt}
                        </Button>
                      ))}
                    </div>

                    {/* Serbest giris */}
                    <Input
                      type="number"
                      min="1"
                      value={creditAmount}
                      onChange={(e) => setCreditAmount(e.target.value)}
                      placeholder="Özel miktar"
                      className="bg-gray-800 border-gray-700 text-white"
                    />

                    <div className="flex gap-3 mt-6">
                      <Button
                        type="button"
                        onClick={() => {
                          const amt = parseInt(creditAmount);
                          if (!amt || amt <= 0) { toast.error("Geçerli bir miktar girin"); return; }
                          handleUpdateQuota(creditUser.id, amt);
                          setShowCreditModal(false);
                          setCreditUser(null);
                        }}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold"
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Ekle
                      </Button>
                      <Button
                        type="button"
                        onClick={() => {
                          setShowCreditModal(false);
                          setCreditUser(null);
                        }}
                        variant="outline"
                        className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-800"
                      >
                        İptal
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* OPARA v28.0: Reels Snapshot Modal */}
          <ReelsSnapshotModal
            isOpen={showReelsSnapshot}
            onClose={() => {
              setShowReelsSnapshot(false);
              setReelsSnapshotUsername("");
            }}
            username={reelsSnapshotUsername}
          />
        </div>
      </div>
    </div>
  );
}

export default AdminPage;
